/**
 * Field Agent Management Page
 *
 * Allows administrators to assign field agents to surveys,
 * set their locations, and view performance statistics.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import * as fieldAgentsApi from './api/fieldAgents'
import * as organizationsApi from './api/organizations'
import './App.css'

/**
 * Debounce hook for rate limiting API calls
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

/**
 * Location Autocomplete Component using OpenStreetMap Nominatim
 */
function LocationAutocomplete({ value, onChange, onSelect, placeholder, required, hasError }) {
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)

  // Debounce the search query (1.5 seconds to respect Nominatim rate limits)
  const debouncedQuery = useDebounce(value, 1500)

  // Fetch suggestions from our backend (which proxies to Nominatim)
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) {
      setSuggestions([])
      return
    }

    const fetchSuggestions = async () => {
      setIsLoading(true)
      try {
        // Use our backend proxy to avoid CORS issues
        const response = await fetch(
          `/api/v1/geocoding/search?q=${encodeURIComponent(debouncedQuery)}&limit=5`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        )

        if (!response.ok) throw new Error('Failed to fetch')

        const data = await response.json()
        // Transform backend response to match the component's expected format
        const transformed = (data.results || []).map((item) => ({
          place_id: item.place_id,
          display_name: item.display_name,
          lat: item.lat,
          lon: item.lon,
          type: item.type,
        }))
        setSuggestions(transformed)
        setShowSuggestions(transformed.length > 0)
        setSelectedIndex(-1)
      } catch (err) {
        console.error('Geocoding search error:', err)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchSuggestions()
  }, [debouncedQuery])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedIndex(-1)
        break
      default:
        break
    }
  }

  // Handle selection
  const handleSelect = (place) => {
    const displayName = place.display_name
    const lat = parseFloat(place.lat)
    const lng = parseFloat(place.lon)

    onChange(displayName)
    onSelect({ name: displayName, lat, lng })
    setShowSuggestions(false)
    setSelectedIndex(-1)
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setShowSuggestions(true)
        }}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%',
          padding: '0.75rem',
          paddingRight: isLoading ? '2.5rem' : '0.75rem',
          border: hasError ? '1px solid #ffc107' : '1px solid #dee2e6',
          borderRadius: '8px',
          fontSize: '1rem',
        }}
      />

      {/* Loading indicator */}
      {isLoading && (
        <span
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#0D9488',
            fontSize: '0.9rem',
          }}>
          ⏳
        </span>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: '250px',
            overflowY: 'auto',
          }}>
          {suggestions.map((place, index) => (
            <div
              key={place.place_id}
              onClick={() => handleSelect(place)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                background: index === selectedIndex ? '#f0f7ff' : 'white',
                transition: 'background 0.15s',
              }}
              onMouseEnter={() => setSelectedIndex(index)}>
              <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '2px' }}>
                {place.display_name.split(',')[0]}
              </div>
              <div
                style={{
                  fontSize: '0.8rem',
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {place.display_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && !isLoading && value.length >= 3 && suggestions.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            padding: '0.75rem 1rem',
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            color: '#666',
            fontSize: '0.9rem',
          }}>
          No locations found. Try a different search term.
        </div>
      )}
    </div>
  )
}

function FieldAgentManagement() {
  const { id: surveyId } = useParams()
  const { user, currentOrg, hasPermission, userRole } = useAuth()

  const [survey, setSurvey] = useState(null)
  const [fieldAgents, setFieldAgents] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Assignment form state
  const [assignForm, setAssignForm] = useState({
    userId: '',
    locationLat: '',
    locationLng: '',
    locationName: '',
    radiusMeters: '',
  })
  const [assigning, setAssigning] = useState(false)

  // Check permissions
  const canManageFieldAgents =
    hasPermission('field_agent_manage') || userRole === 'owner' || userRole === 'administrator'

  // Load data
  useEffect(() => {
    if (!user || !surveyId) return

    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        // Load survey
        const surveyData = await surveysApi.getSurvey(surveyId)
        if (cancelled) return
        setSurvey(surveyData)

        // Load field agents for this survey
        const agents = await fieldAgentsApi.listFieldAgents(surveyId)
        if (cancelled) return
        setFieldAgents(agents)

        // Load organization members if we have an org context
        if (currentOrg) {
          try {
            const allMembers = await organizationsApi.getMembers(currentOrg.id)
            if (cancelled) return
            // Filter to only show field_agent role members (including legacy "viewer" role)
            const fieldAgentMembers = (allMembers || []).filter(
              (m) => m.role_code === 'field_agent' || m.role_code === 'viewer'
            )
            setMembers(fieldAgentMembers)
          } catch (orgErr) {
            console.warn('Could not load organization members:', orgErr)
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load data')
          console.error('Failed to load data:', e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [surveyId, user, currentOrg])

  // Check if location is required (when survey requires field agents)
  const locationRequired = survey?.requires_field_agent || false

  // Handle assign field agent
  const handleAssign = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!assignForm.userId) {
      setError('Please select a field agent')
      return
    }

    // Validate location fields if survey requires field agents
    if (locationRequired) {
      if (!assignForm.locationLat || !assignForm.locationLng) {
        setError('GPS coordinates (latitude and longitude) are required for this survey')
        return
      }
      if (!assignForm.locationName) {
        setError('Location name (city/area) is required for this survey')
        return
      }
    }

    setAssigning(true)
    try {
      await fieldAgentsApi.createFieldAgentLink(surveyId, assignForm.userId, {
        assigned_location_lat: assignForm.locationLat ? parseFloat(assignForm.locationLat) : null,
        assigned_location_lng: assignForm.locationLng ? parseFloat(assignForm.locationLng) : null,
        assigned_location_name: assignForm.locationName || null,
        allowed_radius_meters: assignForm.radiusMeters ? parseInt(assignForm.radiusMeters) : null,
      })

      setSuccess('Field agent assigned successfully!')
      setAssignForm({
        userId: '',
        locationLat: '',
        locationLng: '',
        locationName: '',
        radiusMeters: '',
      })

      // Reload field agents
      const agents = await fieldAgentsApi.listFieldAgents(surveyId)
      setFieldAgents(agents)
    } catch (e) {
      console.error('Failed to assign field agent:', e)
      setError(e.message || 'Failed to assign field agent')
    } finally {
      setAssigning(false)
    }
  }

  // Handle deactivate link
  const handleDeactivate = async (userId) => {
    if (!confirm("Are you sure you want to deactivate this field agent's link?")) {
      return
    }

    try {
      await fieldAgentsApi.deactivateFieldAgentLink(surveyId, userId)
      setSuccess('Field agent link deactivated')

      // Reload field agents
      const agents = await fieldAgentsApi.listFieldAgents(surveyId)
      setFieldAgents(agents)
    } catch (e) {
      console.error('Failed to deactivate:', e)
      setError(e.message || 'Failed to deactivate link')
    }
  }

  // Copy link to clipboard. Reconstruct using current origin to avoid
  // port mismatches between backend-generated short_url and dev server.
  const copyLink = (agent) => {
    let url
    if (agent.short_url) {
      try {
        const path = new URL(agent.short_url, window.location.origin).pathname
        url = `${window.location.origin}${path}`
      } catch {
        url = agent.short_url
      }
    } else {
      url = `${window.location.origin}/run/${surveyId}?token=${agent.unique_token}`
    }
    const agentName = agent.full_name || agent.email
    const message = `📋 Please collect responses for this survey:\n\n${survey?.title || 'Survey'}\n\nYour unique link: ${url}\n\n(This link is assigned to ${agentName || 'you'} for tracking purposes)`
    navigator.clipboard.writeText(message)
    setSuccess('Link copied to clipboard!')
  }

  // Get available agents (not already assigned)
  const assignedUserIds = new Set(fieldAgents.map((a) => a.user_id))
  const availableAgents = members.filter((m) => !assignedUserIds.has(m.user_id))

  if (loading) {
    return (
      <div className="field-agent-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p>Loading field agent management...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!canManageFieldAgents) {
    return (
      <div className="field-agent-page">
        <div className="container">
          <div className="error-state">
            <p>You don't have permission to manage field agents.</p>
            <Link to="/dashboard">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="field-agent-page">
      <div className="container" style={{ maxWidth: '1000px', padding: '2rem' }}>
        {/* Header */}
        <div className="header" style={{ marginBottom: '2rem' }}>
          <Link
            to={`/survey/${surveyId}/settings`}
            style={{ color: '#0D9488', textDecoration: 'none' }}>
            ← Back to Survey Settings
          </Link>
          <h1 style={{ margin: '1rem 0 0.5rem 0' }}>Field Agent Management</h1>
          <h2 style={{ margin: 0, fontWeight: 'normal', color: '#666' }}>{survey?.title}</h2>
        </div>

        {/* Messages */}
        {error && (
          <div
            style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {success}
          </div>
        )}

        {/* Assign Field Agent */}
        <div
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
          }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Assign Field Agent</h3>

          {locationRequired && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#e7f3ff',
                borderRadius: '8px',
                fontSize: '0.9rem',
              }}>
              <strong>📍 Location Required:</strong> This survey requires field agent links for
              access. You must provide the GPS coordinates and location name where this agent will
              collect responses.
            </div>
          )}

          {availableAgents.length === 0 ? (
            <p style={{ color: '#666' }}>
              No available field agents. <Link to="/organization">Invite field agents</Link> to your
              organization first.
            </p>
          ) : (
            <form onSubmit={handleAssign}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Field Agent *
                  </label>
                  <select
                    value={assignForm.userId}
                    onChange={(e) => setAssignForm((prev) => ({ ...prev, userId: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '1rem',
                    }}
                    required>
                    <option value="">Select a field agent...</option>
                    {availableAgents.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.email} {m.full_name ? `(${m.full_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Location Name {locationRequired && <span style={{ color: '#dc2626' }}>*</span>}
                  </label>
                  <LocationAutocomplete
                    value={assignForm.locationName}
                    onChange={(name) => setAssignForm((prev) => ({ ...prev, locationName: name }))}
                    onSelect={(location) => {
                      setAssignForm((prev) => ({
                        ...prev,
                        locationName: location.name,
                        locationLat: location.lat.toFixed(6),
                        locationLng: location.lng.toFixed(6),
                      }))
                    }}
                    placeholder="Start typing a city, address, or landmark..."
                    required={locationRequired}
                    hasError={locationRequired && !assignForm.locationName}
                  />
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                    🔍 Start typing to search. Selecting a location will auto-fill GPS coordinates.
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Latitude {locationRequired && <span style={{ color: '#dc2626' }}>*</span>}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={assignForm.locationLat}
                    onChange={(e) =>
                      setAssignForm((prev) => ({ ...prev, locationLat: e.target.value }))
                    }
                    placeholder="37.7749"
                    required={locationRequired}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border:
                        locationRequired && !assignForm.locationLat
                          ? '1px solid #ffc107'
                          : '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      background: assignForm.locationLat ? '#f0fdf4' : 'white',
                    }}
                  />
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#16a34a' }}>
                    {assignForm.locationLat ? '✓ Auto-filled from location' : ''}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Longitude {locationRequired && <span style={{ color: '#dc2626' }}>*</span>}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={assignForm.locationLng}
                    onChange={(e) =>
                      setAssignForm((prev) => ({ ...prev, locationLng: e.target.value }))
                    }
                    placeholder="-122.4194"
                    required={locationRequired}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border:
                        locationRequired && !assignForm.locationLng
                          ? '1px solid #ffc107'
                          : '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      background: assignForm.locationLng ? '#f0fdf4' : 'white',
                    }}
                  />
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#16a34a' }}>
                    {assignForm.locationLng ? '✓ Auto-filled from location' : ''}
                  </p>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Allowed Radius (meters)
                  </label>
                  <input
                    type="number"
                    value={assignForm.radiusMeters}
                    onChange={(e) =>
                      setAssignForm((prev) => ({ ...prev, radiusMeters: e.target.value }))
                    }
                    placeholder="5000 (default)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Maximum distance (in meters) from GPS coordinates for geofencing alerts. Leave
                    empty to use default (5km).
                  </p>
                </div>
              </div>

              {locationRequired && (
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
                  <span style={{ color: '#dc2626' }}>*</span> Required fields for surveys that
                  require field agent links
                </p>
              )}

              <button
                type="submit"
                disabled={assigning}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#0D9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: assigning ? 'not-allowed' : 'pointer',
                  opacity: assigning ? 0.7 : 1,
                  fontSize: '1rem',
                  fontWeight: '600',
                }}>
                {assigning ? 'Assigning...' : 'Assign Field Agent'}
              </button>
            </form>
          )}
        </div>

        {/* Field Agents List */}
        <div
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            padding: '1.5rem',
          }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Assigned Field Agents</h3>

          {fieldAgents.length === 0 ? (
            <p style={{ color: '#666' }}>No field agents assigned to this survey yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>
                      Field Agent
                    </th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>
                      Location
                    </th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>
                      Survey Link
                    </th>
                    <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>
                      Responses
                    </th>
                    <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>
                      Violations
                    </th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fieldAgents.map((agent, idx) => (
                    <tr
                      key={agent.user_id}
                      style={{
                        borderBottom: '1px solid #dee2e6',
                        background: idx === 0 ? '#f8f9fa' : 'white',
                      }}>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ fontWeight: '600' }}>{agent.full_name || '—'}</div>
                        <div style={{ color: '#666', fontSize: '0.875rem' }}>{agent.email}</div>
                        {idx === 0 && fieldAgents.length > 1 && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginTop: '0.25rem',
                              padding: '0.125rem 0.5rem',
                              background: '#ffc107',
                              color: '#000',
                              borderRadius: '10px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                            }}>
                            🏆 Top Performer
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {agent.assigned_location_name || '—'}
                        {agent.assigned_location_lat && (
                          <div style={{ color: '#666', fontSize: '0.75rem' }}>
                            {agent.assigned_location_lat.toFixed(4)},{' '}
                            {agent.assigned_location_lng.toFixed(4)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {agent.short_url ? (
                          <Link
                            to={(() => {
                              try {
                                return new URL(agent.short_url, window.location.origin).pathname
                              } catch {
                                return `/run/${surveyId}?token=${agent.unique_token}`
                              }
                            })()}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: '#1a56db',
                              wordBreak: 'break-all',
                            }}
                            title={agent.short_url}>
                            {(() => {
                              try {
                                return new URL(agent.short_url, window.location.origin).pathname
                              } catch {
                                return agent.short_url
                              }
                            })()}
                          </Link>
                        ) : (
                          <Link
                            to={`/run/${surveyId}?token=${agent.unique_token}`}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: '#1a56db',
                            }}>
                            /run/{surveyId?.substring(0, 8)}...
                          </Link>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            background: '#dcfce7',
                            color: '#166534',
                            borderRadius: '10px',
                            fontWeight: '600',
                          }}>
                          {agent.responses_count}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {agent.location_violations > 0 ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.75rem',
                              background: '#fee2e2',
                              color: '#dc2626',
                              borderRadius: '10px',
                              fontWeight: '600',
                            }}>
                            {agent.location_violations}
                          </span>
                        ) : (
                          <span style={{ color: '#666' }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <button
                          onClick={() => copyLink(agent)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: '#0D9488',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '0.5rem',
                            fontSize: '0.875rem',
                          }}>
                          📋 Copy Link
                        </button>
                        <button
                          onClick={() => handleDeactivate(agent.user_id)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                          }}>
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FieldAgentManagement
